/**
 * Copyright Mántum All Rights Reserved.
 */
import { Fragment } from 'react';
import Child from '../components/common/field';
import Parent from '../components/common/section';
import { sort, validator } from '@mantum/kanela';

import type {
    Sections,
    Detail,
    Exclude,
    Mode,
    Field,
    Fields,
    Section,
    HttpError,
    Error
} from '@mantum/kanela';

const KEY_MODE = 'mode';
const KEY_VISIBLE = 'visible';

type PropTypes = {
    columns?: number;
    env?: Record<string, unknown>;
    mode?: Mode;
    onChange?: (name?: string, value?: unknown) => void;
    onClear?: (exclude: Exclude) => void;
    onClick?: (name?: string, value?: unknown) => void;
    onInit?: (ref: Detail<unknown>) => void;
    onMessage?: (
        title: string,
        errors?: HttpError | string[] | Error,
        success?: boolean
    ) => void;
    onUnmount?: (name?: string) => void;
    onListener?: (values?: Record<string, unknown>) => void;
    readonly?: boolean;
    sections?: Sections;
    values?: Record<string, unknown>;
};

const variants: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-4'
};

export default function Body(props: PropTypes) {
    // prettier-ignore
    const {
        columns = 1,
        readonly = false
    } = props

    const prepare = (row: unknown) => {
        let visible = true;
        if (!row || typeof row !== 'object') {
            return visible;
        }

        if (KEY_VISIBLE in row) {
            visible = validator.isVisible(row?.[KEY_VISIBLE] as boolean);
        }

        if (props.mode && KEY_MODE in row) {
            const mode = row?.[KEY_MODE] as Record<string, unknown>;
            if (props.mode in mode) {
                const properties = mode[props.mode] as Record<string, unknown>;
                if (properties && KEY_VISIBLE in properties) {
                    visible = validator.isVisible(
                        properties?.[KEY_VISIBLE] as boolean
                    );
                }
            }
        }

        return visible;
    };

    const include = (section: Section, field: Field) => {
        return (
            <Child
                env={props.env}
                field={field}
                key={field.name}
                mode={props.mode}
                onChange={props.onChange}
                onClear={props.onClear}
                onClick={props.onClick}
                onMessage={props.onMessage}
                onUnmount={props.onUnmount}
                onListener={props.onListener}
                readonly={readonly ? readonly : section.readonly}
                ref={props.onInit}
                values={props.values}
            />
        );
    };

    const render = (section: Section, field: Field | Fields) => {
        if (Array.isArray(field)) {
            return (
                <div className='grid w-full grid-cols-1 gap-4 text-left sm:grid-cols-2'>
                    {field
                        .filter(prepare)
                        .sort(sort.asc)
                        .map((it) => include(section, it))}
                </div>
            );
        }
        return include(section, field);
    };

    return (
        <div className={`grid ${variants[columns]} gap-4`}>
            {props.sections
                ?.filter(prepare)
                ?.sort(sort.asc)
                ?.map((section, index) => (
                    <Parent
                        columns={section.columns}
                        description={section.description}
                        icon={section.icon}
                        key={index}
                        name={section.name}
                        open={section.open}
                        separator={section.separator}
                        skeleton={section.skeleton}
                        spaces={section.spaces}
                        testid={section.testid}
                    >
                        {section.fields
                            ?.filter(prepare)
                            ?.sort(sort.asc)
                            ?.map((field, index) => (
                                <Fragment key={index}>
                                    {render(section, field)}
                                </Fragment>
                            ))}
                    </Parent>
                ))}
        </div>
    );
}
